import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft, MoreHorizontal, Eye, CheckCircle, Pencil, ImageIcon, Play, ChefHat, FileText, Loader2, Copy, PlusCircle, Clock, Share2, CloudUpload, Trash2, Save } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Recipe } from "@/types/recipe";
import { uploadApi } from "@/lib/api/services/upload.api";
import { useIsTablet } from "@/hooks/use-mobile";
import { useOrgBranding } from "@/hooks/useOrgBranding";
import { PageHeader } from "@/components/layout/PageHeaderSlot";

interface RecipeDetailHeaderProps {
  recipe: Recipe;
  canManage?: boolean;
  /**
   * View mode only: hide the actions that change the recipe (edit, duplicate,
   * new version, delete). Set in Working mode, where recipes are read-only (YC-33).
   */
  readOnly?: boolean;
  mode?: "view" | "edit";
  onPublish?: () => void;
  onChangeToDraft?: () => void;
  onPreview?: () => void;
  onCookMode?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onCreateVersion?: () => void;
  onViewVersionHistory?: () => void;
  onShare?: () => void;
  onExport?: () => void;
  onDelete?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  onBannerChange?: (imageUrl: string) => void;
  onAvatarChange?: (imageUrl: string) => void;
}

export function RecipeDetailHeader({
  recipe,
  canManage,
  readOnly = false,
  mode = "edit",
  onPublish,
  onChangeToDraft,
  onPreview,
  onCookMode,
  onEdit,
  onDuplicate,
  onCreateVersion,
  onViewVersionHistory,
  onShare,
  onExport,
  onDelete,
  onSave,
  isSaving,
  onBannerChange,
  onAvatarChange,
}: RecipeDetailHeaderProps) {
  const { t } = useLingui();
  const branding = useOrgBranding();
  const recipeCode = `RCP-${recipe.id}`;
  const isTablet = useIsTablet();
  const actionH = isTablet ? "h-11" : "h-13";

  const bannerInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  async function handleUpload(
    file: File,
    setPreview: (url: string | null) => void,
    setUploading: (v: boolean) => void,
    onDone?: (imageUrl: string) => void,
  ) {
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);
    try {
      const result = await uploadApi.uploadImage("recipe", file);
      onDone?.(result.imageUrl);
    } catch {
      toast.error(t`Failed to upload image`);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  }

  // ── Shared actions (reused by the desktop hero row and the tablet top bar) ──
  const moreMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={mode === "view" ? "outline" : "ghost"}
          className={cn(
            actionH,
            "gap-2 px-4 text-base font-medium",
            mode === "view"
              ? "rounded-[0.625rem] border-(--color-border) text-(--color-text-primary)"
              : "text-(--color-text-secondary)",
          )}
        >
          <MoreHorizontal className="size-5" />
          {t`More`}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 p-1.5">
        {mode === "view" && !readOnly && (
          <>
            <DropdownMenuItem onClick={onEdit} className="gap-2 cursor-pointer py-3">
              <Pencil className="size-5 shrink-0" />
              {t`Edit recipe`}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {!readOnly && (
          <>
            <DropdownMenuItem onClick={onDuplicate} className="gap-2 cursor-pointer py-3">
              <Copy className="size-5 shrink-0" />
              {t`Duplicate recipe`}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onCreateVersion} className="gap-2 cursor-pointer py-3">
              <PlusCircle className="size-5 shrink-0" />
              {t`Create new version`}
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem onClick={onViewVersionHistory} className="gap-2 cursor-pointer py-3">
          <Clock className="size-5 shrink-0" />
          {t`View version history`}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onShare} className="gap-2 cursor-pointer py-3">
          <Share2 className="size-5 shrink-0" />
          {t`Share recipe`}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onExport} className="gap-2 cursor-pointer py-3">
          <CloudUpload className="size-5 shrink-0" />
          {t`Export recipe`}
        </DropdownMenuItem>
        {!readOnly && (
          <DropdownMenuItem
            onClick={onDelete}
            className="gap-2 cursor-pointer py-3 text-(--color-danger-text) focus:text-(--color-danger-text)"
          >
            <Trash2 className="size-5 shrink-0" />
            {t`Delete recipe`}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const primaryActions =
    mode === "view" ? (
      <Button
        className={cn(
          actionH,
          "gap-2 pl-4 pr-5 rounded-[0.625rem] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) text-base font-medium",
        )}
        onClick={onCookMode}
      >
        <ChefHat className="size-5" />
        {t`Cook Mode`}
      </Button>
    ) : (
      <>
        {/* Only the general tab holds unsaved edits; the rest persist as you go,
            so a Save there would be a button that can never be pressed. */}
        {onSave && (
          <Button
            variant="outline"
            aria-label={t`Save recipe`}
            className={cn(
              actionH,
              "gap-2 pl-4 pr-5 rounded-[0.625rem] border-(--color-border) text-base font-medium text-(--color-text-primary)",
            )}
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? <Loader2 className="size-5 animate-spin" /> : <Save className="size-5" />}
            {t`Save`}
          </Button>
        )}
        <Button
          variant="outline"
          className={cn(
            actionH,
            "gap-2 pl-4 pr-5 rounded-[0.625rem] border-(--color-border) text-base font-medium text-(--color-text-primary)",
          )}
          onClick={onPreview}
        >
          <Eye className="size-6" />
          {t`Preview`}
        </Button>
        {recipe.isPublished ? (
          <Button
            variant="outline"
            className={cn(
              actionH,
              "gap-2 pl-4 pr-5 rounded-[0.625rem] border-(--color-border) text-base font-medium text-(--color-text-primary)",
            )}
            onClick={onChangeToDraft}
          >
            <FileText className="size-6" />
            {t`Change to Draft`}
          </Button>
        ) : (
          <Button
            className={cn(
              actionH,
              "gap-2 pl-4 pr-5 rounded-[0.625rem] bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) text-base font-medium",
            )}
            onClick={onPublish}
          >
            <CheckCircle className="size-6" />
            {t`Publish`}
          </Button>
        )}
      </>
    );

  // ── Tablet: a compact top-bar header rendered into the app header slot ──
  const tabletHeader = (
    <PageHeader>
      <div className="flex h-15.5 items-center gap-3 border-b border-(--color-border) px-5">
        <Link
          to="/recipes"
          search={{ tab: "prepRecipes", page: 1, pageSize: 50, status: "all" }}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) transition-colors hover:bg-(--color-surface-muted)"
          title={t`Back`}
        >
          <ArrowLeft className="size-5" />
        </Link>
        <h1 className="text-[1.375rem] font-extrabold tracking-tight leading-none text-(--color-text-primary) truncate">
          {recipe.name}
        </h1>
        <span
          className={cn(
            "inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.6875rem] font-bold shrink-0",
            recipe.isPublished
              ? "bg-(--brand-blue) text-white"
              : "bg-(--color-surface-muted) text-(--color-text-secondary)",
          )}
        >
          {recipe.isPublished ? t`Published` : t`Draft`}
        </span>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {moreMenu}
          {primaryActions}
        </div>
      </div>
    </PageHeader>
  );

  return (
    <>
      {isTablet && tabletHeader}
      <div className="flex flex-col w-full">
        {/* Back link — with the organization's logo on the right, because the
            recipe viewer is where a concept's food is presented (T-1301). */}
        {!isTablet && (
          <div className="flex items-center justify-between gap-2 px-3 py-2">
            <Link
              to="/recipes"
              search={{ tab: "prepRecipes", page: 1, pageSize: 50, status: "all" }}
              className="inline-flex items-center gap-2 text-base font-medium text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors"
            >
              <ArrowLeft className="size-5" />
              {t`Back to Recipes`}
            </Link>
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt="" className="h-8 max-w-32 object-contain" />
            )}
          </div>
        )}

        {/* Cover image area. The brand colour underlines it when one is set;
            unset renders no bar at all — the neutral default (T-1301). */}
        <div
          className="relative w-full h-[200px]"
          style={
            branding.primaryColor
              ? { borderBottom: `3px solid ${branding.primaryColor}` }
              : undefined
          }
        >
          <div className="absolute inset-0 overflow-hidden rounded-[12px]">
            {(bannerPreview ?? recipe.imageUrl) ? (
              <img src={bannerPreview ?? recipe.imageUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full bg-(--color-surface-accent)" />
            )}
            {mode === "view" && <div className="absolute inset-0 bg-black/20 rounded-[12px]" />}
          </div>
          {mode === "view" && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="size-[90px] rounded-full bg-white/20 backdrop-blur-[11px] flex items-center justify-center">
                <Play className="size-8 text-white fill-white ml-1" />
              </div>
            </div>
          )}
          {mode === "edit" && canManage && (
            <>
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleUpload(file, setBannerPreview, setBannerUploading, onBannerChange);
                  e.target.value = "";
                }}
              />
              <Button
                variant="brand-outline"
                size="icon"
                disabled={bannerUploading}
                className="absolute top-4 right-4 size-13"
                aria-label={t`Edit cover image`}
                onClick={() => bannerInputRef.current?.click()}
              >
                {bannerUploading
                  ? <Loader2 className="size-5 animate-spin" />
                  : <Pencil className="size-5 text-(--color-text-primary)" />}
              </Button>
            </>
          )}
        </div>

        {/* Profile + info row */}
        <div className="relative flex gap-6 px-8">
          {/* Avatar — overlaps the cover */}
          <div className="relative shrink-0 -mt-10">
            <div className="size-[160px] rounded-[16px] border-4 border-white bg-(--color-surface-accent) overflow-hidden flex items-center justify-center shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)]">
              {(avatarPreview ?? recipe.avatarUrl) ? (
                <img src={avatarPreview ?? recipe.avatarUrl} alt={recipe.name} className="size-full object-cover" />
              ) : (
                <ImageIcon className="size-10 text-(--color-text-secondary)" />
              )}
            </div>
            {mode === "edit" && canManage && (
              <>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleUpload(file, setAvatarPreview, setAvatarUploading, onAvatarChange);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="brand-subtle"
                  size="icon"
                  disabled={avatarUploading}
                  className="absolute -bottom-1 -right-1 size-12 rounded-xl border-[3px] border-white"
                  aria-label={t`Edit profile photo`}
                  onClick={() => avatarInputRef.current?.click()}
                >
                  {avatarUploading
                    ? <Loader2 className="size-5 animate-spin" />
                    : <Pencil className="size-5 text-(--color-text-primary)" />}
                </Button>
              </>
            )}
          </div>

          {/* Recipe info + actions */}
          <div className="flex flex-1 items-start justify-between pt-16">
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-4">
                <h1 className="text-5xl font-semibold text-(--color-text-primary) leading-none">
                  {recipe.name}
                </h1>
                <span
                  className={cn(
                    "inline-flex items-center justify-center px-2 py-1 rounded-full text-(--brand-white) text-sm font-medium leading-none",
                    recipe.isPublished ? "bg-(--brand-blue)" : "bg-(--color-surface-accent)",
                  )}
                >
                  {recipe.isPublished ? t`Published` : t`Draft`}
                </span>
              </div>
              <div className="flex items-center gap-2 text-base font-medium text-(--color-text-secondary)">
                {/* Shown, not editable: the type is chosen once, when the
                    recipe is created, and decides whether it can go on a menu. */}
                <span>{recipe.recipeType === "PLATED" ? t`Plated recipe` : t`Prep recipe`}</span>
                <span>•</span>
                <span>{`V ${recipe.version}.0`}</span>
                <span>•</span>
                <span>{recipeCode}</span>
              </div>
            </div>

            {!isTablet && (
              <div className="flex items-center gap-3">
                {moreMenu}
                {primaryActions}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
